import { clearTokens, readTokens, writeTokens } from './tokenStore';
import type { DeviceTokens } from '@/lib/types';

export class PlayerApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
  ) {
    super(`API ${status}`);
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST';
  body?: unknown;
  query?: Record<string, string | undefined>;
}

let onAuthLoss: (() => void) | null = null;
export function setAuthLossHandler(fn: () => void): void {
  onAuthLoss = fn;
}

let refreshInFlight: Promise<DeviceTokens | null> | null = null;

async function refreshOnce(): Promise<DeviceTokens | null> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = (async () => {
    const t = readTokens();
    if (!t) return null;
    const res = await fetch('/api/v1/devices/refresh', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken: t.refreshToken }),
    });
    if (!res.ok) {
      clearTokens();
      onAuthLoss?.();
      return null;
    }
    const body = (await res.json()) as DeviceTokens;
    writeTokens(body);
    return body;
  })();
  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const base = path.startsWith('/api') ? path : `/api/v1${path}`;
  if (!query) return base;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined) params.append(k, v);
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query);
  const send = async (token: string): Promise<Response> => {
    const init: RequestInit = {
      method: opts.method ?? 'GET',
      headers: {
        accept: 'application/json',
        authorization: `Bearer ${token}`,
        ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
    };
    if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
    return fetch(url, init);
  };

  const t = readTokens();
  if (!t) throw new PlayerApiError(401, { detail: 'no tokens' });

  let res = await send(t.deviceToken);
  if (res.status === 401) {
    const refreshed = await refreshOnce();
    if (!refreshed) throw new PlayerApiError(401, { detail: 'refresh failed' });
    res = await send(refreshed.deviceToken);
  }
  if (!res.ok) {
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    throw new PlayerApiError(res.status, body);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const url = buildUrl(path);
  const res = await fetch(url, {
    method: 'POST',
    headers: { accept: 'application/json', 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    let parsed: unknown;
    try {
      parsed = await res.json();
    } catch {
      parsed = undefined;
    }
    throw new PlayerApiError(res.status, parsed);
  }
  return (await res.json()) as T;
}
